import { useMemo } from 'react';

import { useShowLocation } from '@givebacks/api/offers';
import { Affix, ButtonMantine, Modal, ScrollArea, Stack, Text, useMantineTheme } from 'ui';

import {
  MerchantDetailModalTitle,
  useMerchantDetailModalProvider,
} from '../MerchantDetailModalsProvider';

const MerchantOfferTermsContent = () => {
  const { meta } = useMerchantDetailModalProvider({});

  const { type, onCtaClick, locationId } = meta;
  const { data: locationData } = useShowLocation({
    locationId: locationId,
  });

  const rakutenTerms = useMemo(() => {
    if (type === 'instore' && locationData?.offers[0]?.additional_terms) {
      return locationData?.offers[0]?.additional_terms;
    }
    return null;
  }, [locationData?.offers, type]);

  return (
    <Stack gap="sm" pt="lg">
      <Text type={'body'} fw={500}>
        Terms & Conditions
      </Text>
      <ScrollArea h={500}>
        {rakutenTerms ? (
          <Text c="gray" fz="xs">
            {rakutenTerms}
          </Text>
        ) : (
          <Text c="gray" fz="xs">
            The following is a summary of offer terms and conditions. You can access full terms of
            service here. <br></br> 1. Acceptance of terms. By using the Services, you acknowledge
            that you have read and understood the terms and conditions, and that you agree to be
            legally bound by all of its terms. You also acknowledge that you have had an opportunity
            to review and you agree to the terms of our Privacy Policy. If you do not agree to all
            of the terms and conditions, then you are not allowed to participate in or use the
            Services.
            <br></br> 2. Eligibility. You represent and warrant that you are at least eighteen (18)
            years of age. If you are under age eighteen (18), you may not, under any circumstances
            or for any reason, use the Services.<br></br> 3. Rewards. The services enable you to
            access and earn rewards, subject to the guidelines herein (“Services”). For
            clarification, the rewards covered by this program are not a gift certificate, store
            gift card, or general-use prepaid card. <br></br>a) The Services may include cashback
            and referral bonuses activated by the creation of trackable links (“Links”) for a
            Merchant. Links are configured to track and attribute transactions to you so that you
            may earn a reward if you or someone to whom you send a Link completes a qualifying
            transaction with a Merchant. <br></br>b) Some transactions may not earn rewards or may
            earn at a rate which is different from the rate indicated by the service. You are not
            guaranteed to earn a reward from a transaction for several reasons, without notice to
            you, including, without limitation: i. Merchants may alter terms, conditions, or
            commission rates; <br></br>ii. Merchants may discontinue their programs. <br></br>iii.
            Merchants may exclude certain products from Reward eligibility. <br></br>iv. Merchants
            may have technical failures which result in transactions failing to be tracked or
            attributed to you. <br></br>v. Links may fail to function properly. <br></br>vi. Links
            may be disabled by web browsers or ad blockers. <br></br>vii. Merchants may reject
            transactions due to suspected policy violations. viii. Other: charge-backs, product
            returns, accounting error, Merchant non-payment, fraudulent transactions, our inability
            to contact you in order to validate transactions, your inappropriate distribution of
            Links, or your failure to comply with Merchant policies. <br></br>c) If the Merchant
            pays less than expected amount or does not pay a commission for the transaction, we
            reserve the right, in our sole discretion, to adjust or cancel the reward. Furthermore,
            we reserve the right, in our sole discretion, to withhold, cancel or adjust the amount
            of any reward, for any reason at any time. By using the Services, you accept this
            condition.<br></br> 4. Payout timing. We only provide a reward to you after a
            transaction is successfully tracked and completed and after we receive payment for that
            specific transaction from the Merchant. Merchants usually have holdback periods during
            which they hold a pending payment for a specified period, for example, until the
            expiration of a return policy and/or until a service is consumed. Rewards remain in a
            pending state until we receive payment from the Merchant for the associated transaction.
            After we receive payment from a Merchant for your specific transaction, we remit rewards
            in monthly batches. Rewards may be remitted automatically to your selected payment
            method or we may provide an interface for you to request withdrawal.
            <br></br> 5. Payout Threshold. Withdrawal of confirmed funds may be subject to a minimum
            payout threshold, which may change from time to time, in our sole discretion, without
            notice to you. If there is a payment threshold and your balance is below that threshold,
            rewards will remain in your account and your balance may roll forward to subsequent
            payment periods until the minimum payment threshold is achieved.<br></br> 6. Coupons.
            Some rewards may include coupons, discounts or promotions from Merchants (“Offers”). We
            do not guarantee that merchants will honor Offers. Offer codes may be expired or
            invalid. Offers may not be eligible for your transaction, based upon exclusions, terms
            and conditions determined by Merchants. <br></br>7. Dormant accounts. If you have
            pending rewards, and your account has no new activity (defined as the earning of a
            pending reward) for a period of one hundred and eighty (180) days, we reserve the right
            to assess a monthly Dormant Account Fee of up to $5.00 USD, until such time as your
            reward balance reaches $0.00. Any Dormant Account Fee which we apply may or may not be
            displayed in your user dashboard or other presentations of your account balance. If your
            account has no new activity for a period of 12 months, we reserve the right to void any
            remaining balance of rewards in your account.
            <br></br> 8. Tax. You are obligated to independently assess and comply with all relevant
            tax and legal requirements. We shall not provide you with tax or legal advice.<br></br>{' '}
            9. Cookies. Some Merchants may place cookies (or other forms of tracking beacons) on
            your device to recognize your device during a subsequent visit. If you, or those
            clicking on your shared Link, block or delete cookies, tracking and attribution may be
            inoperable, in which case no reward shall be earned.<br></br> 10. Third-Party Services.
            We do not endorse, warrant or guarantee the products, services or content of Merchants
            or other third-parties. We are not an agent or broker or otherwise responsible for
            Merchants’ activities, policies, products or services.
          </Text>
        )}
      </ScrollArea>
      {type !== 'dining' && (
        <Affix w="100%" withinPortal={false} left={0} right={0} p="lg" bg="white">
          <ButtonMantine fullWidth onClick={onCtaClick}>
            {type === 'online' ? 'Visit Site' : 'Claim Offer'}
          </ButtonMantine>
        </Affix>
      )}
    </Stack>
  );
};
const MerchantOfferTermsModal = () => {
  const { opened, close } = useMerchantDetailModalProvider({
    id: 'Terms',
  });
  const theme = useMantineTheme();

  return (
    <Modal
      opened={opened}
      onClose={close}
      title={
        <MerchantDetailModalTitle
          groupProps={{ w: '100%' }}
          actionIconProps={{ color: 'white', variant: 'transparent' }}
        >
          <Text fz="md" fw={600} w="100%" ta="center">
            Terms & Conditions
          </Text>
        </MerchantDetailModalTitle>
      }
      styles={{
        header: {
          background: theme.colors.slate[6],
          color: 'white',
        },
        title: { width: '100%' },
        close: {
          color: 'white',
        },
      }}
    >
      <MerchantOfferTermsContent />
    </Modal>
  );
};

export { MerchantOfferTermsModal };
